import { createSlice } from '@reduxjs/toolkit'
import userService, { ManufacturerInvite } from '../../services/userService'
import { Dispatch } from 'redux'
import { createNotification } from './notifications'
import { UserAlertRule } from '../../types'
import { User, UserQuery } from '../../types'

interface UsersState {
  users: User[]
  members: User[]
  user: User | undefined
  show: boolean
  error: boolean
  loading: boolean
  showUserAlertManagementPopUp: boolean
  userAlertRules: UserAlertRule[]
}

const initialState: UsersState = {
  users: [],
  members: [],
  user: undefined,
  show: false,
  error: false,
  loading: true,
  showUserAlertManagementPopUp: false,
  userAlertRules: []
}

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsers(state, action) {
      state.users = action.payload
    },
    setUser(state, action) {
      state.user = action.payload
    },
    createUser(state, action) {
      state.users = [...state.users, action.payload]
    },
    updateUser(state, action) {
      state.users = state.users.map((u) => (u.id === action.payload.id ? action.payload : u))
    },
    deleteUser(state, action) {
      state.users = state.users.filter((u) => u.id !== action.payload)
    },
    setLoading(state, action) {
      state.loading = action.payload
    },
    updateUserProject(state, action) {
      state.users = state.users.filter((u) => u.id !== action.payload.id).concat(action.payload)
    },
    setMembers(state, action) {
      state.members = action.payload
    },
    updateMemberState(state, action) {
      state.members = state.members.filter((u) => u.id !== action.payload.id).concat(action.payload)
    },
    deleteMemberState(state, action) {
      state.members = state.members.filter((member) => member.id !== action.payload)
    },
    setShow(state, action) {
      state.show = action.payload
    },
    setShowUserAlertManagementPopUp(state, action) {
      state.showUserAlertManagementPopUp = action.payload
    },
    setUserAlertRules(state, action) {
      state.userAlertRules = action.payload
    },
    deleteUserAlertRule(state, action) {
      state.userAlertRules = state.userAlertRules.filter((u) => u.id !== action.payload)
    }
  }
})

export const {
  setUsers,
  setUser,
  createUser,
  updateUser,
  setShow,
  setShowUserAlertManagementPopUp,
  setUserAlertRules,
  setLoading,
  deleteUserAlertRule,
  updateMemberState,
  deleteMemberState
} = userSlice.actions

export const removeUser = userSlice.actions.deleteUser

export function getUsers(query?: UserQuery) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUsers(await userService.getUsers(query)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getUser(id: number, showDeleted = false) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUser(await userService.getUser(id, showDeleted)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function deleteUser(id: number, permanently = false) {
  return async (dispatch: Dispatch) => {
    try {
      await userService.deleteUser(id, permanently)

      dispatch(userSlice.actions.deleteUser(id))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'success',
          message: 'Succesfully deleted user!',
          timeout: 5000
        })
      )
    } catch (_error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'warning',
          message: 'Something went wrong!',
          timeout: 5000
        })
      )
    }
  }
}

export function getProjectMembers(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(userSlice.actions.setLoading(true))
      const response = await userService.getProjectMembers(id)
      dispatch(userSlice.actions.setMembers(response))
    } finally {
      dispatch(userSlice.actions.setLoading(false))
    }
  }
}

export function inviteManufacturer(email: ManufacturerInvite) {
  return async (dispatch: Dispatch) => {
    try {
      await userService.inviteManufacturer(email)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'success',
          message: 'Email sent succesfully!',
          timeout: 5000
        })
      )
    } catch (_error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'warning',
          message: 'Something went wrong!',
          timeout: 5000
        })
      )
    }
  }
}

export function getUserAlertRulesByProject(userId: number, projectId: number) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUserAlertRules(await userService.getUserAlertRulesByProject(userId, projectId)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getUserAlertRulesByDevice(userId: number, deviceName: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUserAlertRules(await userService.getUserAlertRulesByDevice(userId, deviceName)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export function getUserAlertRulesByRuuviTag(userId: number, ruuviTagId: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUserAlertRules(await userService.getUserAlertRulesByRuuviTag(userId, ruuviTagId)))
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      dispatch(setLoading(false))
    }
  }
}

export default userSlice.reducer
